/**
 * Sector Configuration API Client (v3.5.0)
 * Endpoints for managing user sector preferences
 */

import { apiRequest } from './client';

/**
 * Sector configuration stored on server
 */
export interface SectorConfig {
  id: string;
  name: string;
  enabledSectors: string[];
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/**
 * Available sector with metadata
 */
export interface AvailableSector {
  symbol: string;
  name: string;
  description: string;
}

/**
 * Response from sector config endpoints
 */
export interface SectorConfigResponse {
  success: boolean;
  data?: SectorConfig;
  error?: string;
  timestamp: string;
}

/**
 * Response from available sectors endpoint
 */
export interface AvailableSectorsResponse {
  success: boolean;
  data?: {
    sectors: AvailableSector[];
    total: number;
  };
  timestamp: string;
}

/**
 * Get current sector configuration
 * Automatically migrates from localStorage if needed
 */
export async function getSectorConfig(): Promise<SectorConfig> {
  return apiRequest<SectorConfig>('/api/sectors/config');
}

/**
 * Update sector configuration
 */
export async function updateSectorConfig(enabledSectors: string[]): Promise<SectorConfig> {
  return apiRequest<SectorConfig>('/api/sectors/config', {
    method: 'PUT',
    body: JSON.stringify({ enabledSectors }),
  });
}

/**
 * Reset sector configuration to default (XLK only)
 */
export async function resetSectorConfig(): Promise<SectorConfig> {
  return apiRequest<SectorConfig>('/api/sectors/config', {
    method: 'DELETE',
  });
}

/**
 * Get list of all available sectors with metadata
 */
export async function getAvailableSectors(): Promise<AvailableSector[]> {
  const response = await apiRequest<{ sectors: AvailableSector[]; total: number }>('/api/sectors/available');
  return response.sectors;
}

/**
 * Migrate sector settings from localStorage to server (admin)
 */
export async function migrateSectorSettings(): Promise<{ migrated: boolean; sectors?: string[] }> {
  return apiRequest<{ migrated: boolean; sectors?: string[] }>('/api/sectors/migrate', {
    method: 'POST',
  });
}
